import { WeighingTicketData } from '@/components/WeighingTicket';
import { Pesée } from '@/types/weighing';
import { HistoryPesee } from '@/services/historyService';
import { getDisplayPeseeCode } from '@/utils/ticketSequences';
import { computePoidsNet, resolvePoidsSacs } from '@/utils/weighingNetCalc';

/**
 * Type union pour accepter différents types de pesées
 */
type PeseeInput = Pesée | HistoryPesee | any;

/**
 * Fonction utilitaire pour extraire la référence d'une pesée
 */
const getPeseeReference = (pesee: PeseeInput): string => {
  return getDisplayPeseeCode(pesee);
};

/**
 * Fonction utilitaire pour construire le nom complet du chauffeur
 */
const getChauffeurComplet = (pesee: PeseeInput): string => {
  if (pesee.chauffeur_info) {
    const nom = pesee.chauffeur_info.nom || '';
    const prenom = pesee.chauffeur_info.prenom || '';
    return `${nom} ${prenom}`.trim();
  }
  if (pesee.chauffeur?.nom && pesee.chauffeur?.prenom) {
    return `${pesee.chauffeur.nom} ${pesee.chauffeur.prenom}`.trim();
  }
  return pesee.chauffeur_nom || pesee.chauffeur || '';
};

/**
 * Fonction utilitaire pour extraire le nom du transporteur
 */
const getTransporteurNom = (pesee: PeseeInput): string => {
  return pesee.transporteur_info?.nom || 
         pesee.transporteur?.nom || 
         pesee.transporteur_nom || 
         pesee.transporteur || 
         '';
};

/**
 * Fonction utilitaire pour extraire le nom du convoyeur
 */
const getConvoyeurNom = (pesee: PeseeInput): string => {
  if (pesee.convoyeur_info) {
    const nom = pesee.convoyeur_info.nom || '';
    const prenom = pesee.convoyeur_info.prenom || '';
    return `${nom} ${prenom}`.trim();
  }
  if (pesee.convoyeur?.nom && pesee.convoyeur?.prenom) {
    return `${pesee.convoyeur.nom} ${pesee.convoyeur.prenom}`.trim();
  }
  return pesee.convoyeur_nom || pesee.convoyeur || '';
};

/**
 * Fonction utilitaire pour extraire l'immatriculation de la remorque
 */
const getRemorqueImmatriculation = (pesee: PeseeInput): string => {
  return pesee.remorque_info?.immatriculation || 
         pesee.remorque?.immatriculation || 
         pesee.remorque_numero || 
         pesee.remorque || 
         '';
};

/**
 * Fonction utilitaire pour extraire les données d'analyse qualité
 */
const getAnalyseQualite = (pesee: PeseeInput, qualityAnalysis?: any): WeighingTicketData['analyse_qualite'] => {
  // Prioriser qualityAnalysis passé en paramètre (données externes)
  if (qualityAnalysis) {
    // Vérifier si l'analyse qualité a réellement été faite (au moins un champ significatif rempli)
    const hasValidData = qualityAnalysis.grainnage || 
                        (qualityAnalysis.humidite !== undefined && qualityAnalysis.humidite !== null && qualityAnalysis.humidite !== 0) ||
                        (qualityAnalysis.quality !== undefined && qualityAnalysis.quality !== null && qualityAnalysis.quality !== 0) ||
                        qualityAnalysis.quality_level ||
                        qualityAnalysis.quality_decision;
    
    if (!hasValidData) {
      return undefined;
    }
    
    return {
      grainnage: qualityAnalysis.grainnage || '',
      humidite: qualityAnalysis.humidite !== undefined && qualityAnalysis.humidite !== null
        ? parseFloat(qualityAnalysis.humidite.toString())
        : undefined,
      quality: qualityAnalysis.quality !== undefined && qualityAnalysis.quality !== null
        ? parseFloat(qualityAnalysis.quality.toString())
        : undefined,
      quality_level: qualityAnalysis.quality_level || '',
      quality_decision: qualityAnalysis.quality_decision || '',
      quality_score: qualityAnalysis.quality_score || 0,
      observations: qualityAnalysis.observations || '',
      analysed_by_username: qualityAnalysis.analysed_by_username || pesee.agent_analyse_qualite_nom || '',
      analysed_at: qualityAnalysis.analysed_at || pesee.date_heure || new Date().toISOString()
    };
  }
  
  // Prioriser les données depuis analyse_qualite si elles existent
  if (pesee.analyse_qualite) {
    // Vérifier si l'analyse qualité a réellement été faite
    // NE PAS inclure agent_analyse_qualite car il peut être assigné même sans analyse
    // Il faut au moins un champ de données significatif rempli
    const hasValidData = (pesee.analyse_qualite.grainnage && typeof pesee.analyse_qualite.grainnage === 'string' && pesee.analyse_qualite.grainnage.trim() !== '') || 
                        (pesee.analyse_qualite.humidite !== undefined && pesee.analyse_qualite.humidite !== null && pesee.analyse_qualite.humidite !== 0) ||
                        (pesee.analyse_qualite.quality !== undefined && pesee.analyse_qualite.quality !== null && pesee.analyse_qualite.quality !== 0) ||
                        (pesee.analyse_qualite.quality_level && typeof pesee.analyse_qualite.quality_level === 'string' && pesee.analyse_qualite.quality_level.trim() !== '') ||
                        (pesee.analyse_qualite.quality_decision && typeof pesee.analyse_qualite.quality_decision === 'string' && pesee.analyse_qualite.quality_decision.trim() !== '');
    
    if (!hasValidData) {
      return undefined;
    }
    
    return {
      grainnage: pesee.analyse_qualite.grainnage || (pesee as any).grainnage || '',
      humidite: pesee.analyse_qualite.humidite !== undefined && pesee.analyse_qualite.humidite !== null
        ? parseFloat(pesee.analyse_qualite.humidite.toString())
        : ((pesee as any).humidite !== undefined && (pesee as any).humidite !== null && (pesee as any).humidite !== 0
            ? parseFloat((pesee as any).humidite.toString()) 
            : undefined),
      quality: pesee.analyse_qualite.quality !== undefined && pesee.analyse_qualite.quality !== null
        ? parseFloat(pesee.analyse_qualite.quality.toString())
        : ((pesee as any).quality !== undefined && (pesee as any).quality !== null && (pesee as any).quality !== 0
            ? parseFloat((pesee as any).quality.toString()) 
            : undefined),
      quality_level: pesee.analyse_qualite.quality_level || (pesee as any).quality_level || '',
      quality_decision: pesee.analyse_qualite.quality_decision || '',
      quality_score: pesee.analyse_qualite.quality_score || 0,
      observations: pesee.analyse_qualite.observations || '',
      analysed_by_username: pesee.analyse_qualite.analysed_by_username || pesee.agent_analyse_qualite_nom || '',
      analysed_at: pesee.analyse_qualite.analysed_at || pesee.date_heure || new Date().toISOString()
    };
  }
  
  // Fallback vers les champs directs UNIQUEMENT si des données réelles existent
  // NE PAS utiliser agent_analyse_qualite comme indicateur car il peut être assigné sans analyse
  const hasDirectData = ((pesee as any).grainnage && typeof (pesee as any).grainnage === 'string' && (pesee as any).grainnage.trim() !== '') || 
                        ((pesee as any).humidite !== undefined && (pesee as any).humidite !== null && (pesee as any).humidite !== 0) ||
                        ((pesee as any).quality !== undefined && (pesee as any).quality !== null && (pesee as any).quality !== 0) ||
                        ((pesee as any).quality_level && typeof (pesee as any).quality_level === 'string' && (pesee as any).quality_level.trim() !== '');
  
  if (hasDirectData) {
    return {
      grainnage: (pesee as any).grainnage || '',
      humidite: (pesee as any).humidite !== undefined && (pesee as any).humidite !== null && (pesee as any).humidite !== 0
        ? parseFloat((pesee as any).humidite.toString()) 
        : undefined,
      quality: (pesee as any).quality !== undefined && (pesee as any).quality !== null && (pesee as any).quality !== 0
        ? parseFloat((pesee as any).quality.toString()) 
        : undefined,
      quality_level: (pesee as any).quality_level || '',
      quality_decision: '',
      quality_score: 0,
      observations: '',
      analysed_by_username: pesee.agent_analyse_qualite_nom || '',
      analysed_at: pesee.date_heure || new Date().toISOString()
    };
  }
  
  return undefined;
};

/**
 * Fonction utilitaire pour extraire le nom du produit avec type
 */
const getProductNature = (pesee: PeseeInput): string => {
  return pesee.produit_info?.type_display || 
         pesee.produit_info?.type || 
         '';
};

/**
 * Fonction utilitaire pour extraire le type de conditionnement
 */
const getPackagingType = (pesee: PeseeInput): string => {
  return pesee.produit_info?.conditionnement?.toString() || 
         pesee.conditionnement_info?.nom || 
         pesee.conditionnement_nom || 
         'SACS 50KG';
};

/**
 * Fonction utilitaire pour extraire le taux d'humidité formaté
 */
const getHumidityRateKOR = (pesee: PeseeInput, analyseQualite?: WeighingTicketData['analyse_qualite']): string => {
  if (analyseQualite?.humidite !== undefined && analyseQualite.humidite !== null) {
    return `${parseFloat(analyseQualite.humidite.toString()).toFixed(2)}%`;
  }
  if ((pesee as any).humidite !== undefined && (pesee as any).humidite !== null) {
    return `${parseFloat((pesee as any).humidite.toString()).toFixed(2)}%`;
  }
  return 'N/A';
};

/**
 * Fonction centralisée pour convertir une pesée en WeighingTicketData
 * Cette fonction est utilisée partout dans le projet pour garantir la cohérence
 */
export const convertPeseeToTicketData = (
  pesee: PeseeInput,
  additionalData?: {
    formData?: any;
    siteInfo?: { nom: string; id: string } | null;
    tenantInfo?: { nom: string; id: string } | null;
    pontBasculeInfo?: { nom: string; site: string; tenant: string } | null;
    qualityAnalysis?: any;
  }
): WeighingTicketData => {
  const formData = additionalData?.formData || {};
  const siteInfo = additionalData?.siteInfo;
  const tenantInfo = additionalData?.tenantInfo;
  const pontBasculeInfo = additionalData?.pontBasculeInfo;
  const qualityAnalysis = additionalData?.qualityAnalysis;
  
  // Extraire les données d'analyse qualité (en passant qualityAnalysis si disponible)
  const analyseQualite = getAnalyseQualite(pesee, qualityAnalysis);
  
  // Construire les noms complets
  const chauffeurComplet = getChauffeurComplet(pesee);
  const transporteurNom = getTransporteurNom(pesee);
  const convoyeurNom = getConvoyeurNom(pesee);
  const remorqueImmatriculation = getRemorqueImmatriculation(pesee);
  
  // Extraire les poids
  const poidsBrut = pesee.poids_brut ? parseFloat(pesee.poids_brut.toString()) : 0;
  const poidsTare = pesee.poids_tare ? parseFloat(pesee.poids_tare.toString()) : 0;
  
  // Déterminer le type de pesée
  const typePesee = pesee.type_pesee || formData.typePesee || 'reception';
  const isExpeditionOrTransfert = typePesee === 'expedition' || typePesee === 'transfert';

  const conditionnementCapacite =
    pesee.produit_info?.conditionnement_info?.poids_tare ||
    pesee.conditionnement_info?.poids_tare ||
    pesee.produit?.conditionnement_info?.poids_tare ||
    50;

  const poidsAcceptesStored = pesee.poids_acceptes != null && pesee.poids_acceptes !== ''
    ? parseFloat(pesee.poids_acceptes.toString())
    : (formData.poidsAcceptes ? parseFloat(String(formData.poidsAcceptes)) : 0);

  const sainsCount =
    Number(formData.nbSacsSains ?? pesee.nb_sacs_sains ?? 0) || 0;
  const recondCount =
    Number(formData.nbSacsRecond ?? pesee.nb_sacs_recond ?? 0) || 0;
  // Dès que sains/recond sont renseignés, on ignore un poids_acceptes obsolète
  // (sinon l'impression garde l'ancien net alors que la fiche a été recalculée).
  const hasSacsDetail = !isExpeditionOrTransfert && (sainsCount > 0 || recondCount > 0);

  const poidsSacsCalcules = resolvePoidsSacs({
    typePesee,
    form: {
      nbSacsSains: formData.nbSacsSains,
      nbSacsRecond: formData.nbSacsRecond,
      nbSacsDeclares: formData.nbSacsDeclares,
      nbSacsAcceptes: formData.nbSacsAcceptes,
      // Ne pas injecter un poids_acceptes stocké si sains/recond existent :
      // resolvePoidsSacs doit recalculer (sains + recond×4) × capacité.
      poidsAcceptes: hasSacsDetail ? undefined : formData.poidsAcceptes,
      poidsDeclares: formData.poidsDeclares,
    },
    weighing: hasSacsDetail
      ? { ...pesee, poids_acceptes: undefined, poidsAcceptes: undefined }
      : pesee,
    conditionnementCapacite: Number(conditionnementCapacite) || 50,
  });

  const poidsSacs = hasSacsDetail
    ? poidsSacsCalcules
    : (!Number.isNaN(poidsAcceptesStored) && poidsAcceptesStored > 0
        ? poidsAcceptesStored
        : poidsSacsCalcules);

  const poidsNetStoredRaw =
    pesee.poids_net != null && pesee.poids_net !== ''
      ? parseFloat(pesee.poids_net.toString())
      : null;
  const poidsNetStored =
    poidsNetStoredRaw !== null && !Number.isNaN(poidsNetStoredRaw)
      ? poidsNetStoredRaw
      : null;
  const poidsNetCalculated = computePoidsNet(poidsBrut, poidsTare, poidsSacs);

  // Avec détail sacs : net = formule métier (même que la modification).
  // Sinon : coller à la valeur en base pour coller à la fiche.
  const poidsNet = hasSacsDetail
    ? poidsNetCalculated
    : (poidsNetStored !== null ? poidsNetStored : poidsNetCalculated);

  // Poids brut affiché sur le ticket :
  // correspond au poids pesé en charge - poids pesé tare (différence des deux pesées),
  // indépendamment du poids des sacs (qui est déjà pris en compte dans le poids net).
  const poidsBrutAffiche =
    poidsBrut && poidsTare ? poidsBrut - poidsTare : poidsBrut;
  
  // Extraire les dates distinctes 1ère / 2ème pesée
  const isInverted = (pesee.processus || formData.processus) === 'inverted';
  const dateHeure = pesee.date_heure || pesee.created_at || new Date().toISOString();
  const datePremiere =
    pesee.date_heure_premiere_pesee ||
    dateHeure;

  // Ne jamais réutiliser la 1ère date pour la 2ème : si l'horodatage dédié
  // manque, préférer updated_at / heure_sortie seulement s'ils diffèrent.
  const sameInstant = (a?: string | null, b?: string | null) => {
    if (!a || !b) return false;
    const ta = new Date(a).getTime();
    const tb = new Date(b).getTime();
    if (Number.isNaN(ta) || Number.isNaN(tb)) return a === b;
    return Math.abs(ta - tb) < 1000;
  };

  let dateDeuxieme = pesee.date_heure_deuxieme_pesee || undefined;
  if (!dateDeuxieme || sameInstant(dateDeuxieme, datePremiere)) {
    const candidates = [pesee.updated_at].filter(Boolean) as string[];
    dateDeuxieme =
      candidates.find((c) => !sameInstant(c, datePremiere)) ||
      dateDeuxieme ||
      undefined;
  }
  
  // Construire le ticket data
  const ticketData: WeighingTicketData = {
    id: pesee.id?.toString() || '',
    code: getPeseeReference(pesee),
    sequenceGenerale: pesee.sequence_generale ?? null,
    sequenceAnnuelle: pesee.sequence_annuelle ?? null,
    sequenceMensuelle: pesee.sequence_mensuelle ?? null,
    sequenceJournaliere: pesee.sequence_journaliere ?? null,
    grossWeight: poidsBrutAffiche,
    tareWeight: poidsTare,
    netWeight: poidsNet,
    vehicle: pesee.camion_info?.immatriculation || 
             pesee.camion?.immatriculation || 
             pesee.camion_immatriculation || 
             pesee.camion || 
             formData.vehicule || 
             '',
    driver: chauffeurComplet || formData.chauffeur || '',
    chauffeurCode: pesee.chauffeur_info?.code || 
                   pesee.chauffeur?.code || 
                   pesee.chauffeur_info?.permis || 
                   pesee.chauffeur?.permis || 
                   '',
    product: pesee.produit_info?.nom || 
             pesee.produit?.nom || 
             pesee.produit_nom || 
             pesee.produit || 
             formData.produit || 
             '',
    site: siteInfo?.nom || 
          pontBasculeInfo?.site || 
          pesee.pont_bascule_numéro || 
          'Site principal',
    tenant: tenantInfo?.nom || 
            pontBasculeInfo?.tenant || 
            'Tenant principal',
    grossAt: datePremiere,
    tareAt: dateDeuxieme || '',
    grossUser: pesee.agent_premiere_pesee_nom || 
               pesee.agent_premiere_pesee || 
               'Agent non spécifié',
    tareUser: pesee.agent_deuxieme_pesee_nom || 
              pesee.agent_deuxieme_pesee || 
              'Agent non spécifié',
    analysisUser: pesee.agent_analyse_qualite_nom || 
                  pesee.agent_analyse_qualite || 
                  'Agent non spécifié',
    observations: pesee.observations || formData.observations || '',
    
    // En-tête du ticket
    weighingDate: datePremiere ? new Date(datePremiere).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR'),
    campaignYear: new Date().getFullYear().toString(),
    ticketNumber: getPeseeReference(pesee),
    typePesee: pesee.type_pesee || formData.typePesee || 'reception', // Type de pesée pour le sous-titre
    
    // Section Fournisseur
    supplierIdentity: pesee.fournisseur_info?.nom || 
                      pesee.fournisseur?.nom || 
                      pesee.fournisseur_nom || 
                      pesee.fournisseur || 
                      'NOM DE L\'ENTITE',
    supplierCode: pesee.fournisseur_info?.code_supplier || 
                  pesee.fournisseur?.code_supplier || 
                  '',
    clientCode: pesee.client_info?.nom || 
                pesee.client?.nom || 
                pesee.client_nom || 
                pesee.client || 
                'XXXX-XXXX',
    clientCodeValue: pesee.client_info?.code_client || 
                     pesee.client?.code_client || 
                     '',
    exporterName: pesee.exportateur_info?.nom ||
                  pesee.exportateur?.nom ||
                  pesee.exportateur_nom ||
                  (typeof pesee.exportateur === 'string' ? pesee.exportateur : '') ||
                  '',
    exporterCode: pesee.exportateur_info?.code ||
                  (pesee.exportateur as any)?.code ||
                  '',
    productNature: getProductNature(pesee),
    productOrigin: pesee.provenance || formData.provenance || '',
    bushLotNumber: pesee.numero_lot || formData.numeroLot || '',
    
    // Section Identification
    trailerRegistration: remorqueImmatriculation || formData.remorque || '',
    transferSheetNumber: pesee.numero_transfert || formData.numeroTransfert || 'N° FICHE',
    transporterName: transporteurNom || formData.transporteur || '',
    destination: pesee.destination || formData.destination || '',
    provenance: pesee.provenance || formData.provenance || '',
    stickerNumber: pesee.sticker || formData.sticker || 'N° STICKER',
    
    // Section Poids et Sacherie — 1e/2e = ordre chronologique
    firstWeighingWeight: isInverted
      ? (poidsTare > 0 ? poidsTare : undefined)
      : (poidsBrut > 0 ? poidsBrut : undefined),
    firstWeighingDateTime: datePremiere || undefined,
    secondWeighingWeight: isInverted
      ? (poidsBrut > 0 ? poidsBrut : undefined)
      : (poidsTare > 0 ? poidsTare : undefined),
    secondWeighingDateTime: dateDeuxieme || undefined,
    humidityRateKOR: getHumidityRateKOR(pesee, analyseQualite),
    qualityRefraction: qualityAnalysis?.quality_numbers 
      ? (parseInt(qualityAnalysis.quality_numbers.split('/')[0]) || 0) 
      : 0,
    packagingType: getPackagingType(pesee),
    humidityRefraction: analyseQualite?.humidite || qualityAnalysis?.moisture || undefined,
    // Utiliser le poidsSacs calculé (pour expéditions/transferts = nombre de sacs, pour réceptions = poids acceptés)
    // Ne pas utiliser de valeur de test (50) - si poidsSacs est 0, utiliser 0
    bagWeight: poidsSacs,
    
    // Comptage des sacs
    sacsCount: {
      nb_sacs_declares: pesee.nb_sacs_declares || formData.nbSacsDeclares || 0,
      nb_sacs_sains: pesee.nb_sacs_sains || formData.nbSacsSains || 0,
      nb_sacs_recond: pesee.nb_sacs_recond || formData.nbSacsRecond || 0,
      nb_sacs_humides: pesee.nb_sacs_humides || formData.nbSacsHumides || 0,
      nb_sacs_dechires: pesee.nb_sacs_dechires || formData.nbSacsDechires || 0,
      nb_sacs_rejetes: pesee.nb_sacs_rejetes || formData.nbSacsRejetes || 0,
      // Pour expéditions/transferts, s'assurer que nb_sacs_acceptes est bien inclus
      nb_sacs_acceptes: pesee.nb_sacs_acceptes || formData.nbSacsAcceptes || 
                        (isExpeditionOrTransfert ? (pesee.nb_sacs_sains || pesee.nb_sacs_declares || formData.nbSacsSains || formData.nbSacsDeclares || 0) : 0)
    },
    
    // Données d'analyse qualité (ancien format pour compatibilité)
    qualityAnalysis: qualityAnalysis ? {
      quality_decision: qualityAnalysis.quality_decision,
      quality_numbers: qualityAnalysis.quality_numbers,
      moisture: qualityAnalysis.moisture,
      proteins: qualityAnalysis.proteins,
      hectoliter_weight: qualityAnalysis.hectoliter_weight,
      impurities: qualityAnalysis.impurities,
      broken_grains: qualityAnalysis.broken_grains,
      germinated_grains: qualityAnalysis.germinated_grains,
      observations: qualityAnalysis.observations
    } : undefined,
    
    // Nouveaux champs pour le ticket (compatibilité)
    qualityLevel: analyseQualite?.quality_level || (pesee as any).quality_level || '',
    grainnage: analyseQualite?.grainnage || (pesee as any).grainnage || '',
    quality: analyseQualite?.quality !== undefined && analyseQualite.quality !== null
      ? parseFloat(analyseQualite.quality.toString())
      : ((pesee as any).quality !== undefined && (pesee as any).quality !== null 
          ? parseFloat((pesee as any).quality.toString()) 
          : 0),
    humidite: analyseQualite?.humidite !== undefined && analyseQualite.humidite !== null
      ? parseFloat(analyseQualite.humidite.toString())
      : ((pesee as any).humidite !== undefined && (pesee as any).humidite !== null 
          ? parseFloat((pesee as any).humidite.toString()) 
          : 0),
    poidsAcceptes: poidsSacs || 0,
    
    // Analyse qualité complète (nouveau format)
    analyse_qualite: analyseQualite,
    
    // Signatures
    weighingAgentSignature: 'Agent Peseur',
    clientSupplierSignature: 'Client / Fournisseur',
    
    // Informations des acteurs pour email
    client_nom: pesee.client_info?.nom || pesee.client?.nom || pesee.client_nom || '',
    client_email: pesee.client_info?.email || pesee.client?.email || '',
    fournisseur_nom: pesee.fournisseur_info?.nom || pesee.fournisseur?.nom || pesee.fournisseur_nom || '',
    fournisseur_email: pesee.fournisseur_info?.email || pesee.fournisseur?.email || '',
    exportateur_nom: pesee.exportateur_info?.nom || pesee.exportateur?.nom || pesee.exportateur_nom || '',
    exportateur_email: pesee.exportateur_info?.email || pesee.exportateur?.email || '',
    chauffeur_nom: chauffeurComplet,
    chauffeur_email: pesee.chauffeur_info?.email || pesee.chauffeur?.email || '',
    transporteur_nom: transporteurNom,
    transporteur_email: pesee.transporteur_info?.email || pesee.transporteur?.email || '',
    convoyeur_nom: convoyeurNom,
    convoyeur_email: pesee.convoyeur_info?.email || pesee.convoyeur?.email || ''
  } as any;
  
  return ticketData;
};
